import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import {
  TextField,
  Autocomplete,
  Divider,
  Icon,
  Chip,
  Checkbox,
  Protraction,
  Popper,
  useTheme,
  styled,
  classes,
} from 'bonfire-kindling';
import { useTranslation } from 'react-i18next';
import axios from 'helpers/axios';
import { ORGANIZATIONS_SEARCH } from 'constants/apis';
import debounce from 'lodash/debounce';
import some from 'lodash/some';

const defaultPage = 1;
const defaultLimit = 100;

const selectedGroupName = 'selected group';
const notSelectedGroupName = 'not selected';

const StyledAutocomplete = styled(Autocomplete, {
  shouldForwardProp: prop => prop !== 'showRoot',
})(({ theme, showRoot }) => ({
  [`&.${classes.components.MuiAutocomplete.root}`]: {
    minWidth: '10rem',
    [`& .${classes.components.MuiAutocomplete.inputRoot}[class*="${classes.components.MuiInput.root}"]`]:
      {
        padding: 0,
        [`& .${classes.components.MuiAutocomplete.input}`]: {
          padding: showRoot ? theme.spacing(2) : `${theme.spacing(2)} 0`,
        },
      },
  },
  [`&.${classes.components.MuiAutocomplete.endAdornment}`]: {
    display: 'none',
  },
}));

const StyledPopper = styled(Popper)(({ theme }) => ({
  [`& .${classes.components.MuiAutocomplete.paper}`]: {
    width: '13rem',
  },
  [`& .${classes.components.MuiAutocomplete.listbox}`]: {
    // Override autocomplete's selected item colors, but still allow them to be highlighted
    [`& .${classes.components.MuiAutocomplete.option}`]: {
      "&[aria-selected='true']": {
        backgroundColor: 'rgba(0, 0, 0, 0)',
      },
      "&[data-focus='true']": {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
      fontSize: '0.75rem',
      padding: 0,
    },
    padding: `0.75rem ${theme.spacing(1)}`,
  },
}));

const StyledGroupLabel = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(2)} 0`,
}));

const StyledChip = styled(Chip)(() => ({
  maxWidth: '6rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  margin: '0.15rem',
}));

const StyledLimitTag = styled(Chip)(() => ({
  maxWidth: '3rem',
  margin: '0.15rem',
}));

const StyledCheckbox = styled(Checkbox)(() => ({
  '&:hover': {
    background: 'none',
  },
}));

const StyledTextField = styled(TextField)(() => ({
  [`& .${classes.components.MuiAutocomplete.input}`]: {
    padding: 0,
    minWidth: 0,
  },
}));

const OrganizationDropdown = ({ onChange, value }) => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const [selectedOrgs, setSelectedOrgs] = useState(value);
  const [searchInput, setSearchInput] = useState('');
  const [partialOrgList, setPartialOrgList] = useState([]);
  const [popperOpen, setPopperOpen] = useState(false);

  // Use this loading state in styling
  const [loading, setLoading] = useState(false);

  const fetchOrganizations = useCallback(
    async search => {
      // Only fetching orgs if a search is provided
      if (search) {
        const { data: fetchedOrgList } = await axios.get(ORGANIZATIONS_SEARCH, {
          params: {
            page: defaultPage,
            limit: defaultLimit,
            search,
          },
        });
        setPartialOrgList(
          fetchedOrgList.map(({ OrganizationUUID, OrganizationName }) => ({
            OrganizationUUID,
            OrganizationName,
            // Results from per-project org names from external sources have NULL OrganizationUUID
            key: `${OrganizationUUID}-${OrganizationName}`,
          }))
        );
      } else {
        setPartialOrgList([]);
      }
      setLoading(false);
    },
    [setPartialOrgList]
  );

  const fetchOrganizationsDebounced = useMemo(
    () => debounce(fetchOrganizations, 500, { trailing: true }),
    [fetchOrganizations]
  );

  useEffect(() => {
    if (searchInput) {
      setLoading(true);
    }
    fetchOrganizationsDebounced(searchInput);

    return () => fetchOrganizationsDebounced.cancel();
  }, [fetchOrganizationsDebounced, searchInput]);

  useEffect(() => {
    setSelectedOrgs(value);
  }, [value]);

  const searchOnChange = e => {
    setSearchInput(e.target.value);
  };

  // Combines selected values into the options list, and filters them from the options list to remove duplicates
  const memoizedOptionsList = useMemo(() => {
    const selectedOrgKeys = {};
    selectedOrgs.forEach(org => {
      selectedOrgKeys[org.key] = org;
    });

    const partialOrgsWithoutSelected = partialOrgList.filter(org => !(org.key in selectedOrgKeys));
    return [...selectedOrgs, ...partialOrgsWithoutSelected];
  }, [partialOrgList, selectedOrgs]);

  return (
    <StyledAutocomplete
      multiple
      disableCloseOnSelect
      noOptionsText={
        searchInput
          ? t('opportunities.filters.no_organizations')
          : t('opportunities.filters.search_to_see_organizations')
      }
      showRoot={popperOpen || selectedOrgs.length === 0}
      open={popperOpen}
      value={selectedOrgs}
      inputValue={searchInput}
      loading={loading}
      PopperComponent={StyledPopper}
      // Ignore client side filtering
      filterOptions={options => options}
      options={memoizedOptionsList}
      getOptionLabel={option => option.OrganizationName}
      onChange={(e, newValue, reason) => {
        // Don't remove selected tags if user uses backspace, and don't add undefined values
        // Only case undefined selectedValue is allowed is if nothing has been selected
        const selectedValue = newValue.length > 0 ? newValue[newValue.length - 1] : true;
        if (!(reason === 'remove-option' && e.key === 'Backspace') && selectedValue) {
          setSelectedOrgs(newValue);
          onChange(newValue);
        }
      }}
      disableClearable
      forcePopupIcon={false}
      groupBy={option => {
        if (some(selectedOrgs, option)) {
          return selectedGroupName;
        }
        return notSelectedGroupName;
      }}
      onFocus={() => {
        setPopperOpen(true);
      }}
      onClose={() => {
        setSearchInput('');
        setPopperOpen(false);
      }}
      onOpen={() => {
        setPopperOpen(true);
      }}
      renderGroup={option => {
        return (
          <Fragment key={option.group}>
            {option.group !== selectedGroupName && selectedOrgs.length !== 0 ? (
              <StyledGroupLabel key={option.key}>
                <Divider />
              </StyledGroupLabel>
            ) : (
              <div key={option.key} />
            )}
            {option.children}
          </Fragment>
        );
      }}
      renderTags={(selectedValues, getTagProps) => {
        return !popperOpen && selectedValues.length >= 1 ? (
          <>
            <StyledChip
              {...getTagProps({ index: 0 })}
              variant="outlined"
              label={selectedValues[0].OrganizationName}
              onDelete={null}
              size="small"
            />
            {selectedValues.length > 1 ? (
              <StyledLimitTag
                variant="outlined"
                size="small"
                label={`+${selectedValues.length - 1}`}
              />
            ) : null}
          </>
        ) : null;
      }}
      renderOption={({ key, ...props }, option, { selected }) => {
        return (
          <li key={option.key} {...props}>
            <StyledCheckbox
              icon={
                <Icon color={palette.primary.main} iconName="square" size="large" iconStyle="far" />
              }
              color="default"
              checkedIcon={
                <Icon color={palette.primary.main} iconName="check-square" size="large" />
              }
              checked={selected}
            />
            <Protraction color="inherit" variant="body3">
              {option.OrganizationName}
            </Protraction>
          </li>
        );
      }}
      renderInput={params => (
        <StyledTextField
          {...params}
          InputLabelProps={{
            ...params.InputLabelProps,
            style: value.length ? { color: palette.primary.main } : {},
          }}
          InputProps={{
            ...params.InputProps,
            style: value.length ? { borderColor: palette.primary.main } : {},
          }}
          placeholder={
            popperOpen || selectedOrgs.length === 0 ? t('opportunities.filters.search') : ''
          }
          label="Organization"
          value={searchInput}
          onChange={searchOnChange}
          onClick={() => {
            setPopperOpen(true);
          }}
          onBlur={() => {
            setPopperOpen(false);
            setSearchInput('');
          }}
        />
      )}
    />
  );
};

export default OrganizationDropdown;
